import MaterialTable from 'material-table';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { getAudienceSegments, IAudienceSegment, IUser } from '~/src/dash/api/console-api';
import { IDeal } from '~/src/dash/api/deals';
import { getRoutePath, IInventoryRouteParams, RouteDealEdit } from '~/src/dash/router/routes';
import { obfuscateDealName } from '~/src/dash/utils/obfuscate';

interface IBidderSettingsDealsTableProps {
  deals: IDeal[];
  user: IUser;
  publisherID: string;
}

interface IBidderSettingsDealRow {
  Name: string;
  Channel?: string;
  ChannelGroup?: string;
  AudienceTargeting?: string;
}

export const BidderSettingsDealsTable = (props: IBidderSettingsDealsTableProps) => {
  const { deals, user, publisherID } = props;
  const [segmentMap, setSegmentMap] = useState<{ [key: number]: IAudienceSegment }>({});

  async function loadSegments() {
    try {
      const segments = await getAudienceSegments({
        query: {
          publisher_id: publisherID,
        },
      });

      const segmentMap = segments.reduce((memo: any, segment) => {
        memo[segment.ID] = segment;
        return memo;
      }, {});

      setSegmentMap(segmentMap);
    } catch (e) {
      setSegmentMap({});
    }
  }

  useEffect(() => {
    loadSegments();
  }, [publisherID]);

  const columns = [
    {
      title: 'Deal',
      defaultGroupOrder: 1,
      field: 'Name',
      render: (name: string) => {
        const [ID, Name] = name.split(':');
        const path = getRoutePath(RouteDealEdit, { pub: publisherID } as IInventoryRouteParams, {
          dealID: ID,
        });
        return <Link to={path}> {Name} </Link>;
      },
    },
    {
      title: 'Channels',
      field: 'Channel',
    },
    {
      title: 'Channel Groups',
      field: 'ChannelGroup',
    },
    {
      title: 'Audience Segments',
      field: 'AudienceTargeting',
    },
  ];

  // Unnest deal rows with its associations so that we can use the table's group-by logic
  const dealRowData = deals.flatMap((deal: IDeal) => {
    const Name = `${deal.ID}:${obfuscateDealName(deal, user.Username, deal.Name)}`;
    const dealChannelsRows = deal.Channels.map((channel) => {
      return {
        Name,
        Channel: channel.Name,
        ChannelGroup: '',
        AudienceTargeting: '',
      };
    });

    const dealChannelGroupsRows = deal.ChannelGroups.map((channelGroup) => {
      return {
        Name,
        Channel: '',
        ChannelGroup: channelGroup.Name,
        AudienceTargeting: '',
      };
    });

    let audienceSegmentIDs = [] as number[];
    if (deal.AudienceTargeting && deal.AudienceTargeting.length > 0) {
      audienceSegmentIDs = deal.AudienceTargeting.split(',').map((audienceSegmentID) =>
        parseInt(audienceSegmentID, 10),
      );
    }

    const dealAudienceTargetingRows = audienceSegmentIDs
      .map((audienceSegmentID) => {
        return {
          Name,
          Channel: '',
          ChannelGroup: '',
          AudienceTargeting: segmentMap[audienceSegmentID] && segmentMap[audienceSegmentID].Name,
        };
      })
      .filter((dealRow: IBidderSettingsDealRow) => dealRow.Name);

    const dealRows = [...dealChannelsRows, ...dealChannelGroupsRows, ...dealAudienceTargetingRows];

    // If a deal has no targeting criteria, we add itself so that it will show up in the table
    if (
      dealChannelsRows.length === 0 &&
      dealChannelGroupsRows.length === 0 &&
      dealAudienceTargetingRows.length === 0
    ) {
      dealRows.push({
        Name,
        Channel: 'All',
        ChannelGroup: 'All',
        AudienceTargeting: 'None',
      });
    }

    return dealRows;
  });

  return (
    <MaterialTable
      title='Deal Targeting'
      // TS linter thinks that the name column is incompatible because the custom render function param on a
      // grouped column is different than a normal column
      // @ts-ignore
      columns={columns}
      data={dealRowData}
    />
  );
};
